import Vue from "vue";
import * as Sentry from "@sentry/vue";

// Explicit DSN to ensure the correct one is used
const SENTRY_DSN = process.env.VUE_APP_SENTRY_DSN;

// Get environment from env variables or default to 'local'
const ENVIRONMENT = process.env.VUE_APP_NODE_ENV || "local";

// Check if we are in a local environment
const isLocalEnvironment = ENVIRONMENT === "local";

const SentryService = {
  init() {
    try {
      // Do not initialize Sentry in local environment
      if (isLocalEnvironment) {
        console.log("Entorno local detectado - Sentry no enviará eventos");
        return;
      }

      if (!SENTRY_DSN) {
        console.error("Error: No se ha definido el DSN de Sentry");
        return;
      }

      Sentry.init({
        Vue,
        dsn: SENTRY_DSN,
        sendDefaultPii: true,
        integrations: [
          Sentry.replayIntegration(),
          // Keep BrowserApiErrors enabled but disable its EventTarget
          // add/removeEventListener monkey-patching. That patch wraps all
          // event handlers with sentryWrapped, causing infinite recursion
          // (RangeError: Maximum call stack size exceeded) when password
          // managers (e.g. Kaspersky) or iOS Chrome rapidly trigger focus
          // events on Vuetify 2 text fields. (APP-21)
          Sentry.browserApiErrorsIntegration({ eventTarget: false }),
        ],
        environment: ENVIRONMENT,
        // Debug mode must be false in production. The original check used "prod"
        // but VUE_APP_NODE_ENV is "production" in production builds, so the
        // old condition always evaluated to `true` and flooded the console.
        // (introduced in feat(Logging) commit 912a724 by MatiasJRB, Apr 24 2025)
        debug: ENVIRONMENT !== "production",
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });

      console.log(
        "Sentry inicializado correctamente para entorno:",
        ENVIRONMENT
      );
    } catch (error) {
      console.error("Error al inicializar Sentry:", error);
    }
  },

  captureError(error, context = {}) {
    try {
      // No enviar eventos si estamos en entorno local
      if (isLocalEnvironment) {
        console.error("[LOCAL] Error capturado (no enviado a Sentry):", error);
        console.error("[LOCAL] Contexto:", context);

        return;
      }

      console.log("Capturando error en Sentry:", error);

      Sentry.captureException(error, {
        extra: { ...context },
      });
    } catch (e) {
      console.error("Error al intentar capturar excepción en Sentry:", e);
    }
  },

  captureMessage(message, level = "info", context = {}) {
    try {
      // No enviar eventos si estamos en entorno local
      if (isLocalEnvironment) {
        console.log(
          `[LOCAL] Mensaje (${level}) capturado (no enviado a Sentry):`,
          message
        );
        console.log("[LOCAL] Contexto:", context);
        return;
      }

      console.log("Enviando mensaje a Sentry:", message, level);
      Sentry.captureMessage(message, {
        level,
        extra: { ...context },
      });
    } catch (e) {
      console.error("Error al intentar capturar mensaje en Sentry:", e);
    }
  },

  setUser(user) {
    try {
      // No configurar usuario si estamos en entorno local
      if (isLocalEnvironment) {
        console.log("[LOCAL] Usuario configurado (no enviado a Sentry):", user);
        return;
      }

      if (user && user.id) {
        Sentry.setUser({
          id: user.id,
          email: user.email || "",
          role: user.role_name || "",
          mainRole: user.owner_type
            ? user.owner_type.split("\\")?.[2]?.toLowerCase() ?? ""
            : "",
        });
      } else {
        Sentry.setUser(null);
      }
    } catch (e) {
      console.error("Error al establecer usuario en Sentry:", e);
    }
  },
};

export default SentryService;
